/**
 * Campañas — las campañas de publicidad, lo que vale cada una y lo que trae
 * (27/09/2026). Solo administradores (las directoras): el servidor contesta
 * 403 a los demás (require_role("admin")) y el menú no se lo enseña.
 *
 * Dirección: «crea un apartado campañas donde detalles las campañas que tengamos
 * activas para conocimiento de las directoras» y «lo que vale cada una y datos
 * básicos».
 *
 * Tres decisiones de esta pantalla:
 *
 *  · LO QUE NO SE SABE, «—». Con 0 contactos no hay coste por contacto: ni
 *    infinito, ni 0 €, ni un error. Lo calcula el servidor y aquí se pinta.
 *  · DE DÓNDE SALE CADA CIFRA. El gasto dice si lo puso una persona («a
 *    mano») o lo trajo Meta, y cuándo: un número sin fecha se lee como de hoy.
 *  · LA CONEXIÓN CON META, A LA VISTA Y DESDE AQUÍ. Dirección no toca el
 *    servidor: el token y el secreto se pegan en esta pantalla (y no se
 *    vuelven a ver), la palabra del aviso la genera la app, y una lista de
 *    pasos con ✓/✗ dice qué falta. «Probar ahora» pregunta a Meta sin crear
 *    nada; «Conectar la página» (la de la marca del formulario) la suscribe a
 *    los contactos.
 *
 * Y la PROCEDENCIA de sus contactos (27/09/2026, dirección: «cuando hagamos una
 * campaña de cada cosa, se marca como eso […] para luego tener un buen
 * recuento»): cada fila dice qué campaña de tratamiento da a sus contactos
 * («Campaña <tratamiento>», lo decide el servidor con la misma regla que Leads),
 * y debajo va el recuento por campaña de tratamiento: recibidos, contactados,
 * citados y descartados. «Por el formulario de Meta» es lo que suman los
 * «Contactos» de las filas de ese tratamiento; «Recibidos» lleva además lo que
 * llega por «de dónde viene» o se marca a mano en Leads.
 *
 * Y desde el 16/10/2026 (el análisis de quién se cita):
 *  · «Citados» son los citados POR la campaña: con una cita posterior a cuando
 *    dejaron el formulario, o marcados «Citado» a mano. Los que ya eran
 *    pacientes salen aparte («Ya eran pacientes: N»), sin sumar ni en
 *    «Citados» ni en el «Coste por cita» (gastado ÷ citados). La regla, en el
 *    servidor (services/lead_estado.py).
 *  · «Prioridad de los contactos»: las reglas que marcan «Prioridad alta» en
 *    Leads (components/PrioridadContactosDialog.jsx).
 *
 * Y desde el 31/10/2026:
 *  · «Orden de los contactos» (el diálogo de antes, ahora por NIVELES): en qué
 *    orden sale Leads → Pendientes; el «Prioridad alta» de antes es el nivel 1.
 *  · «Exclusión al entrar»: qué contactos de Meta entran ya descartados, con
 *    su vista previa (components/ExclusionContactosDialog.jsx). Siguen contando
 *    en los «Contactos» de su campaña, como cualquier descartado.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, formatEUR, formatDateTime } from '../lib/api';
import { error as logError } from '../lib/logger';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Loader2, Plus, Pencil, Target, RefreshCw, PlugZap, ExternalLink, Copy, ArrowUpNarrowWide, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import MarcaLead from '../components/MarcaLead';
import ProcedenciaLead from '../components/ProcedenciaLead';
import { useMarcas } from '../lib/marcas';
import RecuentoProcedencia from '../components/RecuentoProcedencia';
import PrioridadContactosDialog from '../components/PrioridadContactosDialog';
import ExclusionContactosDialog from '../components/ExclusionContactosDialog';

const FILTROS = [
  { clave: 'activas', nombre: 'Activas' },
  { clave: 'recientes', nombre: 'Recientes' },
  { clave: 'todas', nombre: 'Todas' },
];

const ESTADOS = {
  activa: { nombre: 'Activa', clase: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  pausada: { nombre: 'Pausada', clase: 'bg-amber-50 text-amber-800 border-amber-200' },
  terminada: { nombre: 'Terminada', clase: 'bg-gray-100 text-gray-600 border-gray-200' },
};

// Las marcas de una campaña: las PROPIAS de la clínica (Configuración →
// Marcas), no las procedencias externas.
function marcasDeCampana(marcas) {
  return (marcas || []).filter((m) => !m.externa);
}

function mensajeError(e, porDefecto) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map((x) => x?.msg || '').filter(Boolean).join(' · ') || porDefecto;
  return porDefecto;
}

/** «2026-09-27» → «27/09/2026», sin pasar por la zona horaria. */
export function fechaCorta(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** El coste por contacto como se lee: «—» si no hay (0 contactos o sin gasto). */
export function costeLegible(coste) {
  return coste === null || coste === undefined || !Number.isFinite(Number(coste))
    ? '—' : formatEUR(coste);
}

function esEnlaceSeguro(url) {
  return /^https?:\/\//i.test(url || '');
}

function presupuestoLegible(c) {
  if (c.presupuesto_total !== null && c.presupuesto_total !== undefined) return formatEUR(c.presupuesto_total);
  if (c.presupuesto_diario !== null && c.presupuesto_diario !== undefined) return `${formatEUR(c.presupuesto_diario)}/día`;
  return '—';
}

function Estado({ estado }) {
  const e = ESTADOS[estado] || ESTADOS.terminada;
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${e.clase}`}
      data-estado={estado}>
      {e.nombre}
    </span>
  );
}

export default function CampanasPage() {
  const [filtro, setFiltro] = useState('activas');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState('');
  const [conexion, setConexion] = useState(null);
  const [falloConexion, setFalloConexion] = useState('');
  const [probando, setProbando] = useState(false);
  const [actualizando, setActualizando] = useState(false);
  // null: cerrado; {}: alta; una campaña: edición.
  const [editando, setEditando] = useState(null);
  // «Orden de los contactos» (16/10/2026; por niveles desde el 31/10/2026).
  const [prioridad, setPrioridad] = useState(false);
  // «Exclusión al entrar» (31/10/2026).
  const [exclusion, setExclusion] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/api/campanas', { params: { filtro } });
      setDatos(r.data || { campanas: [] });
      setFallo('');
    } catch (e) {
      logError('campanas', e);
      // Una lista vacía porque no se ha podido leer no es «no hay campañas».
      setFallo(mensajeError(e, 'No se han podido cargar las campañas'));
    } finally {
      setCargando(false);
    }
  }, [filtro]);

  const cargarConexion = useCallback(async () => {
    try {
      const r = await api.get('/api/campanas/meta/estado');
      setConexion(r.data || null);
      setFalloConexion('');
    } catch (e) {
      setFalloConexion(mensajeError(e, 'No se ha podido leer el estado de la conexión'));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarConexion(); }, [cargarConexion]);

  const probar = async () => {
    if (probando) return;
    setProbando(true);
    try {
      const r = await api.post('/api/campanas/meta/probar');
      if (r.data?.ok) toast.success(r.data.texto || 'Meta contesta');
      else toast.warning(r.data?.texto || 'Meta no ha contestado a todo');
      await cargarConexion();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido probar la conexión'));
    } finally {
      setProbando(false);
    }
  };

  const actualizar = async () => {
    if (actualizando) return;
    setActualizando(true);
    try {
      const r = await api.post('/api/campanas/meta/actualizar');
      const d = r.data || {};
      const texto = `Desde Meta: ${d.actualizadas || 0} actualizada(s), ${d.nuevas || 0} nueva(s)`;
      if (d.errores?.length) toast.warning(`${texto}. ${d.errores[0]}`);
      else toast.success(texto);
      await Promise.all([cargar(), cargarConexion()]);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido actualizar desde Meta'));
    } finally {
      setActualizando(false);
    }
  };

  const campanas = datos?.campanas || [];
  const recuento = datos?.recuento || {};
  // Los contactos por campaña de tratamiento (27/09/2026). Un servidor viejo
  // no lo manda: entonces no se pinta.
  const porProcedencia = datos?.por_procedencia?.filas ? datos.por_procedencia : null;

  return (
    <div className="p-6 space-y-5" data-testid="campanas-page">
      <PageHeader
        title="Campañas"
        subtitle="Las campañas de publicidad: lo que vale cada una y los contactos que trae. Solo administradores."
        actions={
          <div className="flex flex-wrap gap-2">
            {/* En qué orden sale Leads → Pendientes, por niveles (16/10 y 31/10/2026). */}
            <Button variant="outline" onClick={() => setPrioridad(true)} data-testid="campanas-prioridad"
              title="Los niveles con que se ordena Leads → Pendientes (el «Prioridad alta» de antes es el nivel 1)">
              <ArrowUpNarrowWide className="w-4 h-4 mr-2" /> Orden de los contactos
            </Button>
            {/* Qué contactos de Meta entran ya descartados (31/10/2026). */}
            <Button variant="outline" onClick={() => setExclusion(true)} data-testid="campanas-exclusion"
              title="Los contactos de Meta que cumplen estas reglas entran en Leads ya descartados">
              <Filter className="w-4 h-4 mr-2" /> Exclusión al entrar
            </Button>
            <Button onClick={() => setEditando({})} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="campanas-nueva">
              <Plus className="w-4 h-4 mr-2" /> Nueva campaña
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2" role="tablist" data-testid="campanas-filtros">
        {FILTROS.map((f) => {
          const activo = filtro === f.clave;
          const n = recuento[f.clave];
          return (
            <button key={f.clave} type="button" role="tab" aria-selected={activo}
              data-testid={`campanas-filtro-${f.clave}`}
              onClick={() => setFiltro(f.clave)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors ${
                activo ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground'}`}>
              {f.nombre}
              {n !== undefined ? <span className="tabular-nums font-semibold">{n}</span> : null}
            </button>
          );
        })}
      </div>

      {fallo ? (
        <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
          data-testid="campanas-fallo">
          {fallo}. Esta lista no está vacía: es que no se ha podido leer.
        </div>
      ) : cargando && !datos ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : campanas.length === 0 ? (
        <EmptyState icon={Target}
          title={filtro === 'activas' ? 'Ninguna campaña activa' : 'Ninguna campaña'}
          description="Aquí aparecen las campañas de Facebook e Instagram: dalas de alta a mano o tráelas de Meta." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="campanas-tabla">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">Campaña</th>
                <th className="py-2 pr-3">Marca</th>
                <th className="py-2 pr-3">Plataforma</th>
                <th className="py-2 pr-3">Tratamiento</th>
                <th className="py-2 pr-3">Zona y edad</th>
                <th className="py-2 pr-3">Fechas</th>
                <th className="py-2 pr-3 text-right">Presupuesto</th>
                <th className="py-2 pr-3 text-right">Gastado</th>
                <th className="py-2 pr-3 text-right">Contactos</th>
                {/* De esos contactos, cuántos ya tienen cita (27/09/2026): la
                    conversión. Descartar en Leads no resta de ninguna de las dos
                    (dirección, 28/09/2026: lo que trajo la campaña no cambia). */}
                {/* Desde el 16/10/2026, citados POR la campaña: con una cita
                    posterior a cuando preguntaron, o marcados a mano. Los que
                    ya eran pacientes, debajo y aparte. */}
                <th className="py-2 pr-3 text-right">Citados</th>
                <th className="py-2 pr-3 text-right">Coste por contacto</th>
                <th className="py-2 pr-3 text-right">Coste por cita</th>
                <th className="py-2 pr-3">Vista previa</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {campanas.map((c) => (
                <tr key={c.id} className="border-b last:border-0 align-top" data-testid={`campana-${c.id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{c.nombre}</div>
                    <div className="mt-1 flex items-center gap-2"><Estado estado={c.estado} /></div>
                  </td>
                  <td className="py-2 pr-3"><MarcaLead marca={c.marca} testid={`campana-marca-${c.id}`} /></td>
                  <td className="py-2 pr-3">{c.plataforma}</td>
                  <td className="py-2 pr-3">
                    {c.tratamiento}
                    {/* La procedencia que da a sus contactos en Leads. */}
                    {c.procedencia ? (
                      <div className="mt-1">
                        <Link to={`/leads?procedencia=${encodeURIComponent(c.procedencia.clave)}`}
                          className="hover:opacity-80" title="Ver en Leads los contactos de esta procedencia">
                          <ProcedenciaLead procedencia={c.procedencia} testid={`campana-procedencia-${c.id}`} />
                        </Link>
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">
                    {c.zona}
                    {c.edad ? <div className="text-xs text-muted-foreground">{c.edad} años</div> : null}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">
                    {fechaCorta(c.fecha_inicio)}
                    {c.fecha_fin ? ` – ${fechaCorta(c.fecha_fin)}` : ''}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-presupuesto-${c.id}`}>
                    {presupuestoLegible(c)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-gastado-${c.id}`}>
                    {c.gastado === null || c.gastado === undefined ? '—' : formatEUR(c.gastado)}
                    {c.gastado_at ? (
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {c.gastado_origen === 'meta' ? 'Meta' : 'a mano'} · {formatDateTime(c.gastado_at)}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-contactos-${c.id}`}>
                    {c.meta_campaign_id ? (
                      <Link to={`/leads?campana=${encodeURIComponent(c.meta_campaign_id)}`}
                        className="text-[#2E6E5E] hover:underline font-medium"
                        title="Ver estos contactos en Leads">
                        {c.contactos}
                      </Link>
                    ) : c.meta_form_id ? (
                      // Solo con el formulario: los que Meta manda sin campaña.
                      <span title="Los contactos de su formulario de Meta">{c.contactos}</span>
                    ) : (
                      <span title="Sin el id de la campaña ni el del formulario de Meta no se pueden contar sus contactos">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-citados-${c.id}`}>
                    {c.meta_campaign_id ? (
                      <Link to={`/leads?campana=${encodeURIComponent(c.meta_campaign_id)}&vista=citados`}
                        className="text-[#2E6E5E] hover:underline font-medium"
                        title="Ver en Leads los contactos de esta campaña que ya tienen cita">
                        {c.citados ?? 0}
                      </Link>
                    ) : c.meta_form_id ? (
                      <span>{c.citados ?? 0}</span>
                    ) : (
                      <span title="Sin el id de la campaña ni el del formulario de Meta no se pueden contar sus contactos">—</span>
                    )}
                    {c.ya_pacientes ? (
                      <div className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`campana-ya-pacientes-${c.id}`}
                        title="Ya eran pacientes cuando dejaron el formulario (solo tienen citas de antes): no cuentan como citados ni en el coste por cita">
                        Ya eran pacientes: {c.ya_pacientes}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-coste-${c.id}`}>
                    {costeLegible(c.coste_por_contacto)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums" data-testid={`campana-coste-cita-${c.id}`}
                    title="Gastado ÷ citados de la campaña (sin los que ya eran pacientes)">
                    {costeLegible(c.coste_por_cita)}
                  </td>
                  <td className="py-2 pr-3">
                    {esEnlaceSeguro(c.vista_previa_url) ? (
                      <a href={c.vista_previa_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#2E6E5E] hover:underline"
                        data-testid={`campana-vista-${c.id}`}>
                        Ver <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : ''}
                  </td>
                  <td className="py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditando(c)}
                      data-testid={`campana-editar-${c.id}`} title="Editar">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {campanas.some((c) => c.notas) ? (
            <div className="mt-4 space-y-2" data-testid="campanas-notas">
              {campanas.filter((c) => c.notas).map((c) => (
                <p key={c.id} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.nombre}:</span> {c.notas}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Los contactos por campaña de tratamiento (27/09/2026). */}
      {porProcedencia && porProcedencia.filas.length ? (
        <section className="space-y-2" data-testid="campanas-procedencia">
          <h2 className="text-sm font-semibold">Contactos por campaña de tratamiento</h2>
          <p className="text-xs text-muted-foreground">
            Junta los contactos del formulario de Meta de todas las campañas de cada tratamiento
            (lo que suman sus «Contactos» de arriba) con los que llegan por «de dónde viene» o se
            marcan a mano en Leads. «Recibidos» son todos los que han llegado: descartar en Leads
            no lo cambia, y los descartados se ven aparte.
          </p>
          <RecuentoProcedencia filas={porProcedencia.filas} totales={{ campanas: porProcedencia.total }}
            soloCampanas conFormulario testid="campanas-procedencia-tabla" />
        </section>
      ) : null}

      {/* La configuración, debajo de las campañas (dirección, 27/9/2026: «tiene que
          tener arriba la campaña y abajo lo de la conexión con Meta»). */}
      <ConexionMeta estado={conexion} fallo={falloConexion} onCambio={setConexion}
        probando={probando} onProbar={probar}
        actualizando={actualizando} onActualizar={actualizar} />

      {editando ? (
        <FormularioCampana campana={editando} onClose={() => setEditando(null)}
          onGuardada={async () => { setEditando(null); await cargar(); }} />
      ) : null}

      {prioridad ? <PrioridadContactosDialog onClose={() => setPrioridad(false)} /> : null}
      {exclusion ? <ExclusionContactosDialog onClose={() => setExclusion(false)} /> : null}
    </div>
  );
}

function Linea({ etiqueta, children, testid }) {
  return (
    <div className="grid grid-cols-[11rem_1fr] gap-2 py-0.5 text-sm">
      <span className="text-muted-foreground">{etiqueta}</span>
      <span data-testid={testid} className="break-words">{children}</span>
    </div>
  );
}

/** Copia un texto al portapapeles y lo dice. */
async function copiar(texto, que) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${que} copiado`);
  } catch {
    toast.info(`Selecciona ${que.toLowerCase()} y cópialo a mano`);
  }
}

function Copiable({ valor, que, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded bg-muted px-2 py-1 text-xs break-all select-all" data-testid={testid}>{valor}</code>
      <Button type="button" variant="outline" size="sm" onClick={() => copiar(valor, que)}
        data-testid={`${testid}-copiar`}>
        <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
      </Button>
    </div>
  );
}

/**
 * El token o el secreto: se pega aquí y se guarda en el servidor. La
 * pantalla nunca lo vuelve a ver: solo «Puesto ✓ (…últimos 4)».
 */
function CampoSecreto({ campo, etiqueta, ayuda, ficha, onCambio }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [guardando, setGuardando] = useState(false);
  const puesto = ficha?.puesto;
  const delServidor = ficha?.origen === 'entorno';

  const guardar = async () => {
    if (guardando || !valor.trim()) return;
    setGuardando(true);
    try {
      const r = await api.put(`/api/campanas/meta/conexion/${campo}`, { valor });
      setValor('');
      setEditando(false);
      onCambio(r.data);
      toast.success(`${etiqueta}: guardado`);
    } catch (e) {
      toast.error(mensajeError(e, `No se ha podido guardar el ${etiqueta.toLowerCase()}`));
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm(`¿Quitar el ${etiqueta.toLowerCase()}? Sin él, la conexión con Meta se para.`)) return;
    try {
      const r = await api.delete(`/api/campanas/meta/conexion/${campo}`);
      onCambio(r.data);
      toast.success(`${etiqueta}: quitado`);
    } catch (e) {
      toast.error(mensajeError(e, `No se ha podido quitar el ${etiqueta.toLowerCase()}`));
    }
  };

  if (puesto && !editando) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={`meta-${campo}`}>
        <span className="text-emerald-700 font-medium" data-testid={`meta-${campo}-puesto`}>
          Puesto ✓{ficha.final ? ` (…${ficha.final})` : ''}
        </span>
        {delServidor ? (
          <span className="text-xs text-muted-foreground">(lo pone el servidor)</span>
        ) : (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setEditando(true)}
              data-testid={`meta-${campo}-cambiar`}>Cambiar</Button>
            <Button type="button" variant="ghost" size="sm" onClick={quitar}
              data-testid={`meta-${campo}-quitar`}>Quitar</Button>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid={`meta-${campo}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="password" autoComplete="off" spellCheck={false} value={valor}
          onChange={(e) => setValor(e.target.value)} placeholder={`Pega aquí el ${etiqueta.toLowerCase()}`}
          className="max-w-md" data-testid={`meta-${campo}-input`} />
        <Button type="button" size="sm" onClick={guardar} disabled={guardando || !valor.trim()}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={`meta-${campo}-guardar`}>
          {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar
        </Button>
        {puesto ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setEditando(false); setValor(''); }}>
            Cancelar
          </Button>
        ) : null}
      </div>
      {ayuda ? <p className="text-xs text-muted-foreground">{ayuda}</p> : null}
    </div>
  );
}

/**
 * «Conexión con Meta»: los pasos con ✓/✗ (para ver qué falta), el token y el
 * secreto, lo que hay que pegar en Meta y los botones. Al abrirse NO le
 * pregunta nada a Meta.
 */
function ConexionMeta({ estado, fallo, onCambio, probando, onProbar, actualizando, onActualizar }) {
  const [conectando, setConectando] = useState(false);
  if (fallo) {
    return (
      <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
        data-testid="meta-conexion-fallo">{fallo}</div>
    );
  }
  if (!estado) return null;

  const conectar = async () => {
    if (conectando) return;
    setConectando(true);
    try {
      const r = await api.post('/api/campanas/meta/conectar-pagina');
      if (r.data?.ok) toast.success(r.data.texto || 'Página suscrita');
      else toast.error(r.data?.texto || 'Meta no ha suscrito la página');
      const e = await api.get('/api/campanas/meta/estado');
      onCambio(e.data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido conectar la página'));
    } finally {
      setConectando(false);
    }
  };

  const prueba = estado.ultima_prueba;
  const lead = estado.ultimo_lead;
  const error = estado.ultimo_error;
  const sincro = estado.ultima_sincronizacion;
  const pagina = estado.pagina;
  return (
    <section className="cjo-card p-4 space-y-4" data-testid="meta-conexion">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PlugZap className={`w-4 h-4 ${estado.configurado ? 'text-[#2E6E5E]' : 'text-muted-foreground'}`} />
          <span className="font-medium" data-testid="meta-conexion-texto">{estado.texto}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onProbar}
            disabled={!estado.configurado || probando} data-testid="meta-probar"
            title="Pregunta a Meta cuántos contactos hay y si la página está conectada. No crea nada.">
            {probando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Probar ahora
          </Button>
          <Button variant="outline" size="sm" onClick={onActualizar}
            disabled={!estado.token_anuncios || actualizando} data-testid="meta-actualizar"
            title="Trae de Meta el nombre, el estado, las fechas, el presupuesto y lo gastado">
            {actualizando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Actualizar desde Meta
          </Button>
        </div>
      </div>

      {/* Qué está hecho y qué falta, en orden. */}
      <ol className="space-y-1" data-testid="meta-pasos">
        {(estado.pasos || []).map((p, i) => (
          <li key={p.clave} className="flex items-start gap-2 text-sm" data-testid={`meta-paso-${p.clave}`}
            data-hecho={p.hecho ? 'si' : 'no'}>
            <span className={`font-semibold ${p.hecho ? 'text-emerald-700' : 'text-red-600'}`} aria-hidden="true">
              {p.hecho ? '✓' : '✗'}
            </span>
            <span>
              {i + 1}. {p.texto}
              {p.hecho && p.at ? <span className="text-xs text-muted-foreground"> · {formatDateTime(p.at)}</span> : null}
              {!p.hecho && p.detalle ? <span className="block text-xs text-muted-foreground">{p.detalle}</span> : null}
            </span>
          </li>
        ))}
      </ol>

      <div className="space-y-3 border-t pt-3">
        <div>
          <div className="text-sm font-medium mb-1">1. Token de acceso</div>
          <CampoSecreto campo="token" etiqueta="Token de acceso" ficha={estado.token} onCambio={onCambio}
            ayuda="El del usuario del sistema de Meta (Configuración del negocio → Usuarios del sistema → Generar token)." />
        </div>
        <div>
          <div className="text-sm font-medium mb-1">2. Secreto de la app</div>
          <CampoSecreto campo="secreto" etiqueta="Secreto de la app" ficha={estado.secreto} onCambio={onCambio}
            ayuda="En Meta for Developers: tu app → Configuración de la app → Básica → Clave secreta de la app (pulsa «Mostrar»)." />
        </div>
        <div data-testid="meta-webhook">
          <div className="text-sm font-medium mb-1">3. El aviso al momento (webhook)</div>
          <p className="text-xs text-muted-foreground mb-2">
            En Meta for Developers: tu app → Webhooks → «Page» → «Suscribirse a este objeto». Pega estos dos datos
            y pulsa «Verificar y guardar»; después, suscribe el campo «leadgen».
          </p>
          <Linea etiqueta="URL de devolución de llamada">
            <Copiable valor={estado.webhook_url} que="La URL" testid="meta-webhook-url" />
          </Linea>
          <Linea etiqueta="Token de verificación">
            {estado.palabra_verificacion
              ? <Copiable valor={estado.palabra_verificacion} que="El token de verificación" testid="meta-palabra" />
              : '—'}
          </Linea>
          <Linea etiqueta="¿Lo ha comprobado Meta?" testid="meta-webhook-verificado">
            {estado.webhook_verificado_at ? `Sí, el ${formatDateTime(estado.webhook_verificado_at)}` : 'Todavía no'}
          </Linea>
        </div>
        <div>
          <div className="text-sm font-medium mb-1">4. La página {estado.pagina_del || 'de Facebook'}</div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={conectar}
              disabled={!estado.token?.puesto || conectando} data-testid="meta-conectar-pagina">
              {conectando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Conectar la página {estado.pagina_del || 'de Facebook'}
            </Button>
            {pagina?.texto ? (
              <span className={`text-sm ${pagina.suscrita ? 'text-emerald-700' : 'text-red-700'}`}
                data-testid="meta-pagina-resultado">{pagina.texto}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="border-t pt-3">
        <Linea etiqueta="Mientras tanto" testid="meta-plan-b">
          {estado.configurado
            ? `Se lee el formulario cada ${estado.cada_minutos || 30} minutos`
            : 'Parado hasta que haya token'}
          {estado.formularios?.length ? ` (${estado.formularios.join(', ')})` : ''}
        </Linea>
        <Linea etiqueta="Último contacto recibido" testid="meta-ultimo-lead">
          {lead?.at
            ? `${formatDateTime(lead.at)} · ${lead.via === 'webhook' ? 'por el aviso' : 'leyendo el formulario'}${lead.campaign_name ? ` · ${lead.campaign_name}` : ''}`
            : 'Ninguno todavía'}
        </Linea>
        {estado.pendientes ? (
          <Linea etiqueta="Por completar" testid="meta-pendientes">
            {estado.pendientes} contacto(s) de Meta se vuelven a intentar solos, cada vez más espaciado
          </Linea>
        ) : null}
        {estado.descartados ? (
          <Linea etiqueta="Sin datos" testid="meta-descartados">
            {estado.descartados} contacto(s) llegaron de Meta sin nombre, teléfono ni correo y no se han apuntado
          </Linea>
        ) : null}
        <Linea etiqueta="Último error" testid="meta-ultimo-error">
          {error?.at ? `${formatDateTime(error.at)} · ${error.texto}` : 'Ninguno'}
        </Linea>
        {sincro?.at ? (
          <Linea etiqueta="Campañas desde Meta" testid="meta-sincronizacion">
            {`${formatDateTime(sincro.at)} · ${sincro.actualizadas} actualizada(s), ${sincro.nuevas} nueva(s)`}
          </Linea>
        ) : null}
        {prueba?.at ? (
          <Linea etiqueta="Última prueba" testid="meta-ultima-prueba">
            {`${formatDateTime(prueba.at)} · ${prueba.texto || ''}`}
            {(prueba.paginas || []).map((p) => (
              <span key={p.page_id} className="block text-xs" data-testid={`meta-pagina-${p.page_id}`}>
                Página {p.marca_del || p.page_id}:{' '}
                {p.error ? `no se ha podido consultar (${p.error})`
                  : p.suscrita_leadgen ? 'conectada a los contactos ✓' : 'NO está conectada a los contactos'}
              </span>
            ))}
            {(prueba.formularios || []).map((f) => (
              <span key={f.form_id} className="block text-xs">
                Formulario {f.form_id}:{' '}
                {f.error ? `no se ha podido leer (${f.error})`
                  : `${f.total} contacto(s) en Meta, ${f.ya_en_clinicpilot} ya en ClinicPilot`}
              </span>
            ))}
          </Linea>
        ) : null}
      </div>
    </section>
  );
}

const VACIA = {
  nombre: '', marca: '', estado: 'activa', meta_campaign_id: '', meta_form_id: '', plataforma: '',
  tratamiento: '', zona: '', edad: '', fecha_inicio: '', fecha_fin: '',
  presupuesto_total: '', presupuesto_diario: '', gastado: '', vista_previa_url: '', notas: '',
};
const NUMEROS = ['presupuesto_total', 'presupuesto_diario', 'gastado'];

// Los tratamientos de la clínica, para elegir al escribir (27/09/2026): con
// uno de estos, los contactos de la campaña salen en Leads como «Campaña …».
// Uno nuevo también vale, y en Leads aparece su «Campaña …». Salen de lo que
// manda el servidor (GET /api/leads/procedencias): TODOS los tratamientos
// (`tratamientos`, 30/09/2026), tengan ya campaña o no; un servidor de antes
// solo mandaba sus campañas.
export function tratamientosSugeridos(opciones) {
  const conCampana = (opciones?.campanas || [])
    .filter((o) => o.clave !== 'campana:sin-tratamiento')
    .map((o) => o.nombre.replace(/^Campaña /, ''));
  const nombres = Array.isArray(opciones?.tratamientos)
    ? opciones.tratamientos.map((t) => t.nombre) : conCampana;
  return nombres.map((t) => t.charAt(0).toUpperCase() + t.slice(1));
}

function aFormulario(c) {
  const f = { ...VACIA };
  Object.keys(VACIA).forEach((k) => {
    const v = c?.[k];
    f[k] = v === null || v === undefined ? VACIA[k] : String(v);
  });
  return f;
}

/** Un importe como lo escribe una persona: «1000», «1.000», «125,50» o
 *  «1.250,75». «1.000» son mil euros, no uno. */
export function aNumero(texto) {
  const t = (texto ?? '').toString().trim().replace(/\s|€/g, '');
  if (t === '') return null;
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) return Number(t.replace(/\./g, '').replace(',', '.'));
  return Number(t.replace(',', '.'));
}

function aServidor(f) {
  const salida = {};
  Object.keys(VACIA).forEach((k) => {
    const v = (f[k] ?? '').toString().trim();
    if (NUMEROS.includes(k)) salida[k] = aNumero(v);
    else salida[k] = v === '' ? null : v;
  });
  return salida;
}

/** Alta y edición a mano. Al editar solo se manda lo que ha cambiado: así un
 *  gasto que trajo Meta no pasa a «a mano» por guardar otra cosa. */
function FormularioCampana({ campana, onClose, onGuardada }) {
  const nueva = !campana?.id;
  const propias = marcasDeCampana(useMarcas());
  const [f, setF] = useState(() => aFormulario(campana));
  // Una campaña nueva nace con la primera marca propia (si hay).
  useEffect(() => {
    if (nueva && !f.marca && propias.length) setF((x) => ({ ...x, marca: propias[0].clave }));
  }, [nueva, f.marca, propias]);
  const [sugeridos, setSugeridos] = useState([]);
  useEffect(() => {
    let vivo = true;
    api.get('/api/leads/procedencias')
      .then((r) => { if (vivo) setSugeridos(tratamientosSugeridos(r?.data)); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  const [guardando, setGuardando] = useState(false);
  const cambia = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const guardar = async () => {
    if (guardando) return;
    const datos = aServidor(f);
    if (NUMEROS.some((k) => datos[k] !== null && !Number.isFinite(datos[k]))) {
      toast.error('Los importes tienen que ser números (por ejemplo 1000 o 125,50)');
      return;
    }
    setGuardando(true);
    try {
      if (nueva) {
        await api.post('/api/campanas', datos);
        toast.success('Campaña dada de alta');
      } else {
        const antes = aServidor(aFormulario(campana));
        const cambios = {};
        Object.keys(datos).forEach((k) => { if (datos[k] !== antes[k]) cambios[k] = datos[k]; });
        if (!Object.keys(cambios).length) { onClose(); return; }
        await api.put(`/api/campanas/${campana.id}`, cambios);
        toast.success('Campaña guardada');
      }
      await onGuardada();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar la campaña'));
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, etiqueta, props = {}) => (
    <div>
      <Label htmlFor={`campana-${k}`}>{etiqueta}</Label>
      <Input id={`campana-${k}`} value={f[k]} onChange={cambia(k)} data-testid={`campana-form-${k}`} {...props} />
    </div>
  );

  return (
    <Dialog open onOpenChange={(abierta) => { if (!abierta) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campana-dialogo">
        <DialogHeader><DialogTitle>{nueva ? 'Nueva campaña' : 'Editar campaña'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">{campo('nombre', 'Nombre')}</div>
          <div>
            <Label htmlFor="campana-marca">Marca</Label>
            <select id="campana-marca" value={f.marca} onChange={cambia('marca')} data-testid="campana-form-marca"
              className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              {!propias.length ? <option value="">No hay marcas (Configuración → Marcas)</option> : null}
              {propias.map((m) => <option key={m.clave} value={m.clave}>{m.nombre}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="campana-estado">Estado</Label>
            <select id="campana-estado" value={f.estado} onChange={cambia('estado')} data-testid="campana-form-estado"
              className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              {Object.entries(ESTADOS).map(([k, e]) => <option key={k} value={k}>{e.nombre}</option>)}
            </select>
          </div>
          {campo('plataforma', 'Plataforma', { placeholder: 'Facebook e Instagram' })}
          <div>
            {campo('tratamiento', 'Tratamiento', { list: 'campana-tratamientos' })}
            <datalist id="campana-tratamientos">
              {sugeridos.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          {campo('zona', 'Zona')}
          {campo('edad', 'Edad', { placeholder: '20–50' })}
          {campo('fecha_inicio', 'Inicio', { type: 'date' })}
          {campo('fecha_fin', 'Fin', { type: 'date' })}
          {campo('presupuesto_total', 'Presupuesto total (€)', { inputMode: 'decimal' })}
          {campo('presupuesto_diario', 'Presupuesto diario (€)', { inputMode: 'decimal' })}
          {campo('gastado', 'Gastado (€)', { inputMode: 'decimal' })}
          {campo('meta_campaign_id', 'Id de la campaña en Meta', { inputMode: 'numeric' })}
          {/* El formulario instantáneo de la campaña (27/09/2026): los contactos
              que Meta manda sin campaña cuentan en ella por su formulario. */}
          {campo('meta_form_id', 'Id del formulario de Meta', { inputMode: 'numeric' })}
          <div className="md:col-span-2">{campo('vista_previa_url', 'Enlace a la vista previa', { placeholder: 'https://fb.me/…' })}</div>
          <div className="md:col-span-2">
            <Label htmlFor="campana-notas">Notas</Label>
            <Textarea id="campana-notas" value={f.notas} onChange={cambia('notas')} rows={3}
              data-testid="campana-form-notas" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          El gasto que pongas aquí queda como «a mano», con la fecha de hoy. Con el id de Meta,
          los contactos del formulario de esa campaña se cuentan solos, y en Leads salen como
          la campaña de su tratamiento («Campaña rinoplastia»); un tratamiento nuevo trae su
          propia «Campaña …». Con el id del formulario, también cuentan los que Meta manda sin
          campaña; si no lo pones, «Actualizar desde Meta» lo trae cuando puede.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando || !f.nombre.trim()}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="campana-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {nueva ? 'Dar de alta' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
